// Money is stored as integer laari (MVR x 100); qty is 3dp (architecture.md §7).

import type { Item, Site, StockLocation } from "../api-models";

export function fmtMVR(laari: number | null | undefined): string {
  if (laari == null) return "-";
  return (laari / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " MVR";
}

export function mvrToLaari(mvr: number): number {
  return Math.round(mvr * 100);
}

export function laariToMvr(laari: number | null | undefined): number | undefined {
  return laari == null ? undefined : laari / 100;
}

export function fmtQty(qty: number | null | undefined, unit?: string): string {
  if (qty == null) return "-";
  const n = qty.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 3 });
  return unit ? `${n} ${unit}` : n;
}

// An item is never shown by name alone: names are neither unique nor precise enough to
// identify stock, so the code always leads. Tables give the code its own column instead;
// this is for running text — select options, titles, descriptions, alerts.
export function fmtItem(item?: Pick<Item, "code" | "name"> | null, fallback = "-"): string {
  if (!item) return fallback;
  if (!item.code) return item.name || fallback;
  if (!item.name) return item.code;
  return `${item.code} — ${item.name}`;
}

// Same rule for the flattened `itemCode`/`itemName` pair report rows carry.
export function fmtItemFields(code?: string | null, name?: string | null, fallback = "-"): string {
  return fmtItem(code || name ? { code: code ?? "", name: name ?? "" } : null, fallback);
}

// A site is always shown under its project — the plain-string form of SiteLabel, for the
// print sheet and anywhere else that takes text rather than nodes.
export function fmtSite(site?: Site | null, fallback = ""): string {
  if (!site) return fallback;
  return site.project ? `${site.project.name} / ${site.name}` : site.name;
}

// A store is shown under the project that owns it, the same way a site is — the
// plain-string form of LocationLabel, for select options and other text-only spots.
// Central and sink locations have no site, so they stand on their own name.
export function fmtLocation(location?: StockLocation | null, fallback = ""): string {
  if (!location) return fallback;
  const project = location.site?.project;
  return project ? `${project.name} / ${location.name}` : location.name;
}
